import { DataSeries, DataSeriesParameter } from '@next/core-lib/models';

export interface CycleBarnReport {
  cycleNumber: number;
  cycleDate: string;
  barns: Barn[];
}

export interface Barn {
  id: number;
  name: string;
  surfaceArea: string;
  pdf: PdfFile;
}

export interface PdfFile {
  id: string;
  fileName: string;
  locale: string;
}

export interface GetCycleBarnReportsResponse {
  cycleBarnReports: CycleBarnReport[];
}

export interface NormalizedFlockDataObject {
  cycleId?: number;
  cycleNumber?: number;
  barnId?: number;
  barnName?: string;
  breeds?: string;
  locationId?: number;
  locationName?: string;
  fromDate?: Date;
  toDate?: Date;
  dayOldChicks?: number;
  dayOldChicksPrice?: number;
  dayOldChicksPriceExclDiscounts?: number;
  totalDeparture?: number;
  departureWeight?: number;
  slaughterWeight?: number;
  revenue?: number;
  revenueExclDiscounts?: number;
  feedBalance?: number;
  feedBalancePrice?: number;
  feedBalancePriceExclDiscounts?: number;
  lossPercentage?: number;
  replacementPercentage?: number;
  feedConversion?: number;
  feedConversion1500?: number;
  averageWeightInGram?: number;
  weightedAverageAgeInDays?: number;
  weightedAverageGrowthPerDay?: number;
  productionNumber?: number;
  dayOldChicksPerSquareMeter?: number;
  feedUsageInKgPerSquareMeter?: number;
  feedConsumptionPricePer100Kg?: number;
  paidAmountPerKg?: number;
  nettoRevenue?: number;
  nettoRevenuePerChick?: number;
  nettoRevenuePerSquareMeter?: number;
  nettoRevenuePerSquareMeterPerDay?: number;
  rejectionRate?: number;
  deliveredKgPerSquareMeter?: number;
  nettoProfitInclOtherCosts?: number;
}

export interface NormalizedTimeSeries {
  cycleId?: number;
  cycleNumber?: number;
  barnId?: number;
  barnName?: string;
  breeds?: string;
  kpiValues: NormalizedValue[];
  locationId?: number;
  locationName?: string;
  fromDate?: Date;
  toDate?: Date;
}

export interface NormalizedValue {
  parameter: DataSeriesParameter;
  value: number;
  dateTime: Date;
}

export interface GetFlockDataRequest {
  startDate?: string;
  endDate?: string;
  locationIds: number[];
  cycleIds: number[];
  barnIds: number[];
  parameters: FlockDataParameter[];
  dataViewType: DataViewType;
  conceptId: number;
}

export interface TimeSeriesResponse {
  data: DataSeries<number>;
}

export enum DataViewType {
  PerCycleBarn = 'PerCycleBarn',
  PerCycle = 'PerCycle',
  PerLocation = 'PerLocation',
  PerLocationBarn = 'PerLocationBarn',
}

export enum FlockDataParameter {
  DayOldChicks = 'DayOldChicks',
  DayOldChicksWeight = 'DayOldChicksWeight',
  DayOldChicksPrice = 'DayOldChicksPrice',
  DayOldChicksPriceExclDiscounts = 'DayOldChicksPriceExclDiscounts',
  TotalDeparture = 'TotalDeparture',
  PartialDeparture = 'PartialDeparture',
  FullDeparture = 'FullDeparture',
  DepartureWeight = 'DepartureWeight',
  SlaughterWeight = 'SlaughterWeight',
  Revenue = 'Revenue',
  RevenueExclDiscounts = 'RevenueExclDiscounts',
  FeedBalance = 'FeedBalance',
  FeedBalancePrice = 'FeedBalancePrice',
  FeedBalancePriceExclDiscounts = 'FeedBalancePriceExclDiscounts',
  FeedBalanceRawProduct = 'FeedBalanceRawProduct',
  FeedBalanceMixProduct = 'FeedBalanceMixProduct',
  FeedBalanceRawPrice = 'FeedBalanceRawPrice',
  FeedBalanceMixPrice = 'FeedBalanceMixPrice',
  WeightedHeelDermatitis = 'WeightedHeelDermatitis',
  WeightedFootpadLesionNonePercentage = 'WeightedFootpadLesionNonePercentage',
  WeightedFootpadLesionModeratePercentage = 'WeightedFootpadLesionModeratePercentage',
  WeightedFootpadLesionSeverePercentage = 'WeightedFootpadLesionSeverePercentage',
  TotalLoss = 'TotalLoss',
  LossPercentage = 'LossPercentage',
  WeightedFootpadLesionScore = 'WeightedFootpadLesionScore',
  ReplacementPercentage = 'ReplacementPercentage',
  FeedConversion = 'FeedConversion',
  FeedConversion1500 = 'FeedConversion1500',
  AverageWeightInGram = 'AverageWeightInGram',
  WeightedAverageAgeInDays = 'WeightedAverageAgeInDays',
  WeightedAverageGrowthPerDay = 'WeightedAverageGrowthPerDay',
  ProductionNumber = 'ProductionNumber',
  DayOldChicksPerSquareMeter = 'DayOldChicksPerSquareMeter',
  FeedUsageInKgPerSquareMeter = 'FeedUsageInKgPerSquareMeter',
  PricePerDayOldChick = 'PricePerDayOldChick',
  FeedConsumptionPricePer100Kg = 'FeedConsumptionPricePer100Kg',
  PaidAmountPerKg = 'PaidAmountPerKg',
  NettoRevenue = 'NettoRevenue',
  NettoRevenuePerChick = 'NettoRevenuePerChick',
  NettoRevenuePerSquareMeter = 'NettoRevenuePerSquareMeter',
  NettoRevenuePerSquareMeterPerDay = 'NettoRevenuePerSquareMeterPerDay',
  FeedConsumptionPerDeliveredKg = 'FeedConsumptionPerDeliveredKg',
  RejectionRate = 'RejectionRate',
  DeliveredKgPerSquareMeter = 'DeliveredKgPerSquareMeter',
  NettoProfitInclOtherCosts = 'NettoProfitInclOtherCosts',
}

export const FLOCK_DATA_FILTERS: FlockDataParameter[] = [
  FlockDataParameter.DayOldChicks,
  FlockDataParameter.DayOldChicksPrice,
  FlockDataParameter.DayOldChicksPriceExclDiscounts,
  FlockDataParameter.TotalDeparture,
  FlockDataParameter.DepartureWeight,
  FlockDataParameter.SlaughterWeight,
  FlockDataParameter.Revenue,
  FlockDataParameter.RevenueExclDiscounts,
  FlockDataParameter.FeedBalance,
  FlockDataParameter.FeedBalancePrice,
  FlockDataParameter.FeedBalancePriceExclDiscounts,
  FlockDataParameter.LossPercentage,
  FlockDataParameter.ReplacementPercentage,
  FlockDataParameter.FeedConversion,
  FlockDataParameter.FeedConversion1500,
  FlockDataParameter.AverageWeightInGram,
  FlockDataParameter.WeightedAverageAgeInDays,
  FlockDataParameter.WeightedAverageGrowthPerDay,
  FlockDataParameter.ProductionNumber,
  FlockDataParameter.DayOldChicksPerSquareMeter,
  FlockDataParameter.FeedUsageInKgPerSquareMeter,
  FlockDataParameter.FeedConsumptionPricePer100Kg,
  FlockDataParameter.PaidAmountPerKg,
  FlockDataParameter.NettoRevenue,
  FlockDataParameter.NettoRevenuePerChick,
  FlockDataParameter.NettoRevenuePerSquareMeter,
  FlockDataParameter.NettoRevenuePerSquareMeterPerDay,
  FlockDataParameter.RejectionRate,
  FlockDataParameter.DeliveredKgPerSquareMeter,
  FlockDataParameter.NettoProfitInclOtherCosts,
];
